import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ws } from "@/lib/ws";
import type { JobMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";
import { isBinaryExpired, BINARY_RETENTION_DAYS } from "@/lib/jobs";
import {
  COMPILED_LINES_TOOLTIP,
  DynamicCodeBadge,
  LINES_OF_CODE_TOOLTIP,
  fmtLines,
} from "@/components/CodeMetrics";

/**
 * FIXED 2026-08-24, direct operator directive: this page used to try to
 * stream a "Live log" via job.log/jobs.subscribe — a server-side stub
 * that never sent anything (see ws/handlers/jobs.ts's own header), so it
 * silently hung forever showing nothing. Beyond just being broken: full
 * compiler build logs are NOT something customers should see at all —
 * that's operator-only (private/auth's jobsAdminRoutes.ts, gated by
 * requireOperator — see that file's own security-fix header for the
 * incident this drew a hard line from). What a customer gets here
 * instead: status, timing, the job id itself (for support tickets — it's
 * already right there in the URL/heading), and a download link for the
 * finished binary while it's within its 30-day retention window.
 */
function statusVariant(status: JobMeta["status"]) {
  switch (status) {
    case "succeeded":
      return "success" as const;
    case "failed":
      return "destructive" as const;
    case "running":
      return "default" as const;
    default:
      return "secondary" as const;
  }
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<JobMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadPending, setDownloadPending] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setJob(null);
    setError(null);
    const offJob = ws.on("jobs.job", (msg) => {
      if (msg.job.id === id) setJob(msg.job);
    });
    const offStatus = ws.on("job.status", (msg) => {
      if (msg.job_id === id)
        setJob((j) => (j ? { ...j, status: msg.status } : j));
    });
    const offError = ws.on("error", (msg) => {
      if (msg.req_type === "jobs.get") setError(msg.message);
    });
    ws.send({ type: "jobs.get", job_id: id });
    return () => {
      offJob();
      offStatus();
      offError();
    };
  }, [id]);

  useEffect(() => {
    if (!downloadPending || !id) return;
    const offUrl = ws.on("jobs.downloadUrl", (msg) => {
      if (msg.job_id !== id) return;
      setDownloadPending(false);
      window.open(msg.url, "_blank", "noopener");
    });
    const offErr = ws.on("error", (msg) => {
      if (msg.req_type === "jobs.download") {
        setDownloadPending(false);
        setDownloadError(msg.message);
      }
    });
    return () => {
      offUrl();
      offErr();
    };
  }, [downloadPending, id]);

  const expired = job ? isBinaryExpired(job) : false;

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center gap-4">
        <Link to="/jobs" className="text-sm underline text-muted-foreground">
          ← Jobs
        </Link>
      </div>
      <div>
        <h1 className="text-2xl font-semibold font-mono">{id}</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Job ID — include this if you contact support about this build.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {job && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>{job.entry}</CardTitle>
              <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
            </div>
            <CardDescription>{job.source_summary}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Submitted by</dt>
                <dd>
                  {job.submitted_by
                    ? (job.submitted_by.name ?? job.submitted_by.email)
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">API key</dt>
                <dd>{job.api_key_name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Created</dt>
                <dd>{relativeTime(job.created_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Finished</dt>
                <dd>{job.finished_at ? relativeTime(job.finished_at) : "—"}</dd>
              </div>
              <div>
                <dt
                  className="text-muted-foreground cursor-help"
                  title={LINES_OF_CODE_TOOLTIP}
                >
                  Lines of code
                </dt>
                <dd className="tabular-nums">
                  {fmtLines(job.total_lines_of_code)}
                </dd>
              </div>
              <div>
                <dt
                  className="text-muted-foreground cursor-help"
                  title={COMPILED_LINES_TOOLTIP}
                >
                  Compiled lines of code
                </dt>
                <dd className="tabular-nums">
                  {fmtLines(job.compiled_lines_of_code)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Dynamic code</dt>
                <dd>
                  <DynamicCodeBadge value={job.dynamic_code} />
                </dd>
              </div>
            </dl>

            {job.status === "failed" && (
              <p className="text-sm text-muted-foreground">
                This build failed. Contact support with the job ID above if you
                need help diagnosing why.
              </p>
            )}

            {job.status === "succeeded" && (
              <div className="flex items-center gap-3">
                {expired ? (
                  <Badge variant="secondary">
                    Expired — binaries are retained for {BINARY_RETENTION_DAYS}{" "}
                    days
                  </Badge>
                ) : (
                  <Button
                    disabled={downloadPending}
                    onClick={() => {
                      setDownloadError(null);
                      setDownloadPending(true);
                      ws.send({ type: "jobs.download", job_id: job.id });
                    }}
                  >
                    {downloadPending
                      ? "Preparing download…"
                      : "Download binary"}
                  </Button>
                )}
              </div>
            )}
            {downloadError && (
              <p className="text-sm text-destructive">{downloadError}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
